import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { usePageMeta, organizationSchema, serviceSchema, faqSchema, breadcrumbSchema } from '../lib/seo';
import { industries, offers } from '../data/siteData';
import {
  ButtonLink,
  Card,
  Container,
  CTASection,
  DemoCallout,
  FAQList,
  FeatureList,
  PageHero,
  PriceCard,
  Section,
  SectionHeading,
} from '../components/SiteUi';

export function TradesHubPage() {
  usePageMeta({
    title: 'AI Receptionists for Australian Trades – CloudNext',
    description: 'AI receptionists and websites for plumbers, electricians, HVAC, mowing and cleaning businesses. Answer calls, collect enquiries and book jobs.',
    path: '/industries/trades',
    jsonLd: [
      organizationSchema(),
      serviceSchema({ name: 'AI Receptionist for Trades', serviceType: 'AI receptionist that collects enquiries for trades', path: '/industries/trades', description: 'AI receptionist we set up and run and website setup for Australian trades.' }),
      breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Trades', path: '/industries/trades' }]),
    ],
  });

  return (
    <>
      <PageHero
        eyebrow="Industries"
        title={<>Built for trades that cannot <span className="text-gold-gradient">answer the phone.</span></>}
        description="Plumbers, electricians, HVAC, mowing and cleaning. The same problem: the call arrives while you are on the tools, driving or closed."
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Trades' }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/ai-receptionist">See the AI receptionist</ButtonLink>
          <ButtonLink to="/contact" variant="secondary">Book a call</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <SectionHeading eyebrow="Choose your trade" title="How calls are handled changes with the job." description="An emergency plumbing call is not the same as a mowing quote. The questions, urgency and booking rules are different." />
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {industries.map((industry) => (
              <Link key={industry.slug} to={`/industries/${industry.slug}`} className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition hover:-translate-y-0.5 hover:border-gold/40">
                <h3 className="font-display text-xl text-cream group-hover:text-gold-light">{industry.name}</h3>
                <p className="mt-3 text-sm leading-6 text-dust">{industry.intro}</p>
                <span className="mt-5 inline-block text-sm text-gold-light">See the {industry.name.toLowerCase()} setup →</span>
              </Link>
            ))}
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Common ground" title="What every trade setup needs." />
              <FeatureList
                className="mt-6"
                items={[
                  'Clear call handling for new enquiries, bookings and reschedules',
                  'Service area and availability rules',
                  'Emergency calls passed to a person, and a human when needed',
                  'A booking that lands in the calendar or job system',
                  'A summary the team can act on',
                ]}
              />
            </div>
            <div>
              <DemoCallout />
            </div>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Pricing" title="Start with one trade, one setup." />
          <div className="mt-10 max-w-xl">
            <PriceCard offer={offers.find((offer) => offer.key === 'websiteChatbotVoice')} featured />
          </div>
        </Container>
      </Section>

      <CTASection title="Which trade is first?" description="Trades are the first focus. Tell us what you do and we will tell you whether the AI receptionist fits." primaryTo="/contact" primaryLabel="Book a call" secondaryTo="/ai-receptionist" secondaryLabel="See the AI receptionist" />
    </>
  );
}

export function IndustryPage() {
  const { slug } = useParams();
  const industry = industries.find((item) => item.slug === slug);
  const path = industry ? `/industries/${industry.slug}` : '/404';
  const faqs = industry ? industry.faqs : [];

  usePageMeta({
    title: industry ? `AI Receptionist for ${industry.name} in Australia – CloudNext` : 'Page not found – CloudNext',
    description: industry ? `AI phone answering, taking enquiries and appointment booking for ${industry.metaLabel || industry.name.toLowerCase()} businesses in Australia. Set up and managed by CloudNext.` : 'Page not found.',
    path,
    jsonLd: industry
      ? [
        organizationSchema(),
        serviceSchema({ name: `AI Receptionist for ${industry.name}`, serviceType: `AI receptionist for ${industry.name.toLowerCase()}`, path, description: `AI receptionist we set up and run for ${industry.name.toLowerCase()} businesses in Australia.`, audience: `${industry.name} businesses` }),
        faqSchema(faqs),
        breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Trades', path: '/industries/trades' }, { name: industry.name, path }]),
      ]
      : [organizationSchema()],
  });

  if (!industry) return <Navigate to="/404" replace />;

  return (
    <>
      <PageHero
        eyebrow={`For ${industry.name}`}
        title={<>AI receptionist for <span className="text-gold-gradient">{industry.name.toLowerCase()}.</span></>}
        description={industry.intro}
        breadcrumbs={[{ name: 'Home', to: '/' }, { name: 'Trades', to: '/industries/trades' }, { name: industry.name }]}
      >
        <div className="flex flex-col gap-3 sm:flex-row">
          <ButtonLink to="/demo">Hear the demo</ButtonLink>
          <ButtonLink to="/contact" variant="secondary">Book a call</ButtonLink>
        </div>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="What gets missed" title="The calls you cannot get to." />
              <FeatureList className="mt-6" items={industry.painPoints} />
            </div>
            <div>
              <SectionHeading eyebrow="What the AI handles" title="The calls it can take." />
              <FeatureList className="mt-6" items={industry.scenarios} />
            </div>
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <div className="grid gap-10 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="What it captures" title="The details you need to quote and book." />
              <FeatureList className="mt-6" items={industry.captures} />
            </div>
            <Card>
              <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-gold-light">How it works</p>
              <FeatureList
                className="mt-4"
                items={[
                  'We map how calls are handled for your trade.',
                  'We configure the calendar, service area and rules.',
                  'We test normal, urgent, after-hours and calls passed to a person.',
                  'We go live and review the first weeks with you.',
                ]}
              />
            </Card>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Pricing" title={`A ${industry.name.toLowerCase()} setup.`} description="The final quote depends on how your calls are handled and which systems you use." />
          <div className="mt-10 max-w-xl">
            <PriceCard offer={offers.find((offer) => offer.key === 'websiteChatbotVoice')} featured />
          </div>
        </Container>
      </Section>

      <Section className="border-y border-white/[0.04] bg-ink-light/40">
        <Container>
          <DemoCallout />
        </Container>
      </Section>

      <Section>
        <Container>
          <SectionHeading eyebrow="Questions" title={`${industry.name} questions.`} />
          <div className="mt-8"><FAQList items={faqs} /></div>
        </Container>
      </Section>

      <CTASection title={`Want an AI receptionist for your ${industry.name.toLowerCase()} business?`} description="Tell us how calls arrive now and what you want booked. We will tell you what fits." primaryTo="/contact" primaryLabel="Book a call" secondaryTo="/pricing" secondaryLabel="See pricing" />
    </>
  );
}
